<template>
    <div class="flex flex-col h-full w-full">
        <transaction-actions
            class="self-end"
            :transaction="transaction"
            transaction-view
            :on-click-delete="handleDelete"
        />

        <div class="overflow-y-auto flex flex-col gap-4">
            <table class="w-full table">
                <thead>
                    <tr>
                        <th>Dato</th>
                        <th>Beskrivelse</th>
                        <th>Konto</th>
                        <th>MVA</th>
                        <th v-if="hasProject">Prosjekt</th>
                        <th v-if="hasDepartment">Avdeling</th>
                        <th class="text-right">Beløp</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(group, groupIndex) in visibleGroups" :key="groupIndex">
                        <tr
                            v-if="group.header"
                            class="bg-elevated/30"
                            data-cy="transaction-event"
                        >
                            <td :colspan="columnCount" class="py-2 px-2">
                                <div class="flex items-center gap-2 text-sm text-muted">
                                    <span :class="[group.header.icon, 'size-3.5 shrink-0']" />
                                    <span class="font-semibold text-default">{{ group.header.label }}</span>
                                    <span>·</span>
                                    <span>{{ group.header.date }}</span>
                                </div>
                            </td>
                        </tr>
                        <tr
                            v-for="(line, lineIndex) in group.lines"
                            :key="line.uuid"
                            :class="line.deleted ? 'text-sm' : ''"
                            :data-cy="`transaction-line-${lineIndex}`"
                        >
                            <td data-cy="date">{{ formattedDate(line.date) }}</td>
                            <td>
                                <div
                                    v-if="line.deleted || line.correction || line.corrected"
                                    data-cy="description-badge"
                                    class="flex flex-wrap gap-2"
                                >
                                    <badge v-if="line.deleted" variant="soft" color="error">Slettet</badge>
                                    <badge v-if="line.correction" variant="soft" color="error">Tilbakeføring</badge>
                                    <badge v-if="line.corrected" variant="soft" color="error">Tilbakeført</badge>
                                </div>
                                <span data-cy="description">{{ line.description }}</span>
                            </td>
                            <td>
                                <transaction-account-cell :transaction-line="line"/>
                            </td>
                            <td data-cy="vat">{{ getVatOptionById(line.vatOptionId)?.code || 0 }}</td>
                            <td v-if="hasProject" data-cy="project">{{ line.project }}</td>
                            <td v-if="hasDepartment" data-cy="department">{{ line.department }}</td>
                            <td
                                class="text-right"
                                :class="line.amount < 0 ? 'text-error' : ''"
                                :data-cy="`amount`"
                            >
                                {{ formatCurrency(line.amount) }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>

            <button
                v-if="hasCorrections"
                class="text-sm px-3 py-1.5 rounded-md border border-default bg-default hover:bg-elevated text-default cursor-pointer font-medium self-start"
                @click="showCorrections = !showCorrections"
            >
                {{ showCorrections ? 'Skjul korrigeringer' : 'Vis korrigeringer' }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, onUnmounted, PropType, ref} from 'vue'
import Badge from '@nuxt/ui/components/Badge.vue'
import {Transaction} from '@/types/transaction/Transaction'
import {formatCurrency, formatDateTime, formattedDate} from '@/services/formatter.service'
import {useVatService} from '@/vat/vat.service'
import TransactionAccountCell from '@/transaction/edit-transaction/timeline/TransactionAccountCell.vue'
import {deleteTransaction} from '@/transaction/transaction.service'
import emitter, {useEmitter} from '@/services/infrastructure/emitter'
import {DELETE_TRANSACTION} from '@/types/transaction/TransactionEvent'
import TransactionActions from '@/transaction/TransactionActions.vue'
import {DocumentMeta} from '@/types/transaction/DocumentMeta'
import {AuditLogEntry} from '@/types/AuditLog'

const {getVatOptionById} = useVatService()
const {cleanUp} = useEmitter()

const props = defineProps({
    transaction: {
        type: Object as PropType<Transaction>,
        required: true
    },
    auditEntries: {
        type: Object as PropType<AuditLogEntry[]>,
        required: true
    },
    allDocumentMetas: {
        type: Object as PropType<DocumentMeta[]>,
        required: true
    }
})

onUnmounted(() => {
    cleanUp()
})

// Default to showing corrections for deleted transactions (full history view)
const showCorrections = ref(props.transaction?.deleted ?? false)

const hasCorrections = computed(() =>
    (props.transaction?.transactionLines ?? []).some(l => l.correction || l.corrected)
)

const tableGroups = computed(() => {
    const lines = props.transaction?.transactionLines ?? []
    const entries = props.auditEntries ?? []

    const createdEntry = entries.find(e => e.action === 'created')
    const latestUpdate = [...entries]
        .filter(e => e.action === 'updated')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]

    const originalLines = lines.filter(l => !l.correction && !l.corrected)
    const correctionLines = lines.filter(l => l.correction || l.corrected)

    const groups = []

    if (originalLines.length) {
        const label = createdEntry
            ? `Opprettet av ${createdEntry.actor.display || createdEntry.actor.identifier}`
            : 'Opprettet'
        const date = createdEntry
            ? formatDateTime(createdEntry.createdAt)
            : props.transaction?.createdAt
                ? formatDateTime(props.transaction.createdAt)
                : ''
        groups.push({ header: { icon: 'i-ph-sparkle', label, date }, lines: originalLines })
    }

    if (correctionLines.length) {
        const label = latestUpdate
            ? `Endret av ${latestUpdate.actor.display || latestUpdate.actor.identifier}`
            : 'Endret'
        const date = latestUpdate ? formatDateTime(latestUpdate.createdAt) : ''
        groups.push({ header: { icon: 'i-ph-pencil', label, date }, lines: correctionLines })
    }

    return groups
})

const visibleGroups = computed(() =>
    showCorrections.value
        ? tableGroups.value
        : tableGroups.value.filter(g => g.lines.every(l => !l.correction && !l.corrected))
)

const hasProject = computed(() =>
    props.transaction.transactionLines.some((tl) => tl.project)
)

const hasDepartment = computed(() =>
    props.transaction.transactionLines.some((tl) => tl.department)
)

const columnCount = computed(() =>
    5 + (hasProject.value ? 1 : 0) + (hasDepartment.value ? 1 : 0)
)

const handleDelete = async () => {
    if (props.transaction) {
        await deleteTransaction(props.transaction.uuid)
        emitter.emit(DELETE_TRANSACTION, props.transaction)
    }
}
</script>

<style scoped>
</style>
